import Layout from '@components/Layout';
import { ReactElement } from 'react';
import { HeaderText, SmallPageHeader } from '@components/Text';
import { FullBleedPadding } from '@components/ContentPadding';
import { BuyObscuraMintPass3 } from '@components/BuyButtons';
import { routes } from '@utils/routes';
import { ButtonLink } from '@components/Link';
import { GetStaticPropsContext } from 'next';
import { getClient } from 'db/contentAPI';
import { gql } from '@apollo/client';
import { GetHowItWorksPageQuery } from 'generated/graphql';
import FrequentlyAskedQuestions from '@components/FrequentlyAskedQuestions';
import { COMMISSIONS } from 'logic/commissions';
import { useQueryWithFallback } from 'hooks/useDataWithFallback';
import { PATRON_PASS_PAGE_ID } from 'logic/pages';

export default function HowItWorks({
  data,
}: {
  data: GetHowItWorksPageQuery;
}): ReactElement {
  const page = useQueryWithFallback(queryHowItWorks, data);
  const styles = { calloutSectionTitles: 'font-semibold mb-4 sm:text-2xl' };
  return (
    <Layout>
      <div className="bg-white overflow-hidden">
        <FullBleedPadding className="mx-auto text-center justify-around flex flex-wrap">
          <div className="w-full">
            <SmallPageHeader className="mb-5 text-left">
              {page.howItWorks.headline}
            </SmallPageHeader>
            <p className="text-left w-1/2 text-lg">
              Through the purchase of an Obscura Patron Pass, you are directly
              supporting the creation of new work through grants and
              commissions, mentorships, and exhibitions.
            </p>
            <div className='w-px mt-5' >
              <BuyObscuraMintPass3
                tier={COMMISSIONS.curated}
                text="Buy Patron Pass"
                buttonColor="bg-black"
                textStyle='text-white'
                buttonStyle='h-12 px-5'
              />
            </div>
          </div>
        </FullBleedPadding>
        <FullBleedPadding className="bg-gray-300 mt-16 py-20">
          <h3 className="font-semibold text-4xl">
            Obscura Patron Passes give you a front row seat in the Obscura
            ecosystem.
          </h3>
          <div className="grid sm:grid-cols-2 mt-14 md:grid-cols-4 gap-y-14 gap-x-8 sm:gap-x-6">
            <div>
              <p className={styles.calloutSectionTitles}>Photographs</p>
              <p>
                Own eight 1 of 1 highly curated photography NFTs from world
                class artists
              </p>
            </div>
            <div>
              <p className={styles.calloutSectionTitles}>Patronage</p>
              <p>
                As a collector, you become a patron that allows Obscura to
                expand its mission in a sustainable way, create more grants, and
                support emerging artists.
              </p>
            </div>
            <div>
              <p className={styles.calloutSectionTitles}>Exclusive access</p>
              <ul>
                <li className="mb-1">
                  • Connect with renowned artists in an exclusive
                  Collectors&apos; Lounge
                </li>
                <li className="mb-1">
                  • Meet up and coming artists that aren&apos;t on the radar of
                  other collectors yet
                </li>
                <li className="mb-1">
                  • Collect limited edition signed prints, books, and surprise
                  airdropped NFTs.
                </li>
                <li className="mb-1">
                  • Be a guest at exclusive in-person events
                </li>
              </ul>
            </div>
            <div>
              <p className={styles.calloutSectionTitles}>Participation</p>
              <p>
                As an Obscura Patron Pass holder, you can help us decide which
                projects to support and create impact as a collector.
              </p>
            </div>
          </div>
        </FullBleedPadding>
        <FullBleedPadding className="py-20 bg-black text-white">
          {/* TODO: cms */}
          <HeaderText as="h2" className="mb-5 text-white">
            Join us in building a new world of photography by acquiring an
            Obscura Patron Pass
          </HeaderText>
          <div className=" mt-14 gap-y-14 gap-x-8 sm:gap-x-4">
            <div>
              <p className={styles.calloutSectionTitles}>Curated Patron Pass</p>
              <p className="mb-4">
                Obscura commissions world class artists to produce new native
                NFT photographs with full creative autonomy, exclusively for
                Curated Patron Pass holders.
              </p>
              <p className="mb-4">
                A Curated Patron Pass guarantees you eight 1 of 1 NFT&apos;s,
                hand selected by Obscura&apos;s team of curators. Patron Pass
                proceeds support sizable commissions to Curated Artists and
                enable grants to emerging photographers in the NFT space.
              </p>
              <p className="mb-4">Cost: 3 ETH each | Supply: 55 Passes</p>
            </div>
            <div>
              <BuyObscuraMintPass3
                tier={COMMISSIONS.curated}
                textStyle="text-black"
                buttonColor="bg-white"
                text="Buy Patron Pass"
              />
            </div>
          </div>
        </FullBleedPadding>
        <FullBleedPadding className="mt-12 mb-24">
          <h3 className="font-semibold text-4xl">
            Looking to buy a pass on the{' '}
            <a href="https://opensea.io/collection/obscura-mint-pass">
              secondary market?
            </a>{' '}
            Be sure to check what commissions have been claimed for the pass
            you&apos;re considering before purchase.
          </h3>
          <div className="grid md:grid-cols-2 mt-3 gap-y-14 gap-x-8 sm:gap-x-4">
            <div>
              <ButtonLink href={routes.passAvailability('')}>
                Check Here
              </ButtonLink>
            </div>
            <div></div>
          </div>
        </FullBleedPadding>
        <FrequentlyAskedQuestions faq={page.howItWorks.faqCollection.items} />
      </div>
    </Layout>
  );
}

const queryHowItWorks = gql`
  query GetHowItWorksPage($preview: Boolean) {
    howItWorks(id: "${PATRON_PASS_PAGE_ID}", preview: $preview) {
      headline
      faqCollection {
        items {
          question
          answer
        }
      }
    }
  }
`;

export async function getStaticProps({
  preview,
  previewData,
}: GetStaticPropsContext) {
  const { data, error } = await getClient(
    (previewData as { api: string })?.api
  ).query({
    query: queryHowItWorks,
    variables: {
      preview,
    },
  });

  if (error) {
    console.error(
      `Failed to fetch how it works, received ${JSON.stringify(error)}`
    );
  }

  return {
    props: {
      data,
      fallback: true,
      preview: preview ?? false,
      revalidate: 120,
    },
  };
}
